/**
 * FileSystemService.js
 * 
 * Service for managing drafts in the local filesystem
 * Uses FileSaver.js for browser download functionality
 */

import { saveAs } from 'file-saver';

// Draft tracking key in localStorage
const DRAFT_TRACKING_KEY = 'dota2_draft_tracking';
const DRAFT_FOLDER_KEY = 'dota2_draft_folder';
const DRAFT_FOLDER_HANDLE_KEY = 'dota2_draft_folder_handle';

/**
 * Save a draft to the local filesystem
 * 
 * @param {Object} draftData - The draft data to save
 * @returns {Promise<string>} - The ID of the saved draft
 */
const saveDraft = async (draftData) => {
  try {
    // Generate a new ID if needed
    const draftId = draftData.id || `draft_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    
    // Update metadata
    const draftToSave = {
      ...draftData,
      id: draftId,
      createdAt: draftData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Generate filename based on team, tournament, stage, and date
    const cleanString = (str) => {
      return str ? str.replace(/[^a-z0-9]/gi, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') : '';
    };
    
    const teamName = cleanString(draftToSave.teamName) || 'Team';
    const tournamentName = cleanString(draftToSave.tournament) || 'Tournament';
    const stageName = cleanString(draftToSave.stage) || 'Stage';
    const dateString = draftToSave.date || new Date().toISOString().slice(0, 10);
    
    // Add timestamp suffix if this is a new draft (no original ID passed) to avoid conflicts
    const isNewDraft = !draftData.id;
    const timestampSuffix = isNewDraft ? `_${Date.now()}` : '';
    
    const filename = `${teamName}_${tournamentName}_${stageName}_${dateString}${timestampSuffix}.json`;
    
    // Create a blob with the draft data
    const jsonContent = JSON.stringify(draftToSave, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    
    // Save the file using FileSaver
    saveAs(blob, filename);
    
    // Store references to drafts in localStorage for tracking
    trackSavedDraft(draftId, filename, draftToSave);
    
    // Cache the draft data for future quick loading
    try {
      localStorage.setItem(`draft_cache_${draftId}`, jsonContent);
      console.log('Draft cached successfully:', draftId);
    } catch (cacheError) {
      console.warn('Could not cache draft data', cacheError);
    }
    
    return draftId;
  } catch (error) {
    console.error('Error saving draft to filesystem:', error);
    throw new Error('Failed to save draft locally: ' + error.message);
  }
};

/**
 * Track saved draft information in localStorage
 * This helps maintain a list of drafts without reading the filesystem
 */
const trackSavedDraft = (draftId, filename, draftData) => {
  try {
    // Get existing draft tracking info
    const tracking = getDraftTracking();
    
    // Generate display title that matches filename format (without .json extension and timestamp)
    const cleanString = (str) => {
      return str ? str.replace(/[^a-z0-9]/gi, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') : '';
    };
    
    const teamName = cleanString(draftData.teamName) || 'Team';
    const tournamentName = cleanString(draftData.tournament) || 'Tournament';
    const stageName = cleanString(draftData.stage) || 'Stage';
    const dateString = draftData.date || new Date().toISOString().slice(0, 10);
    
    const displayTitle = `${teamName}_${tournamentName}_${stageName}_${dateString}`;
    
    // Update with new draft info
    tracking[draftId] = {
      id: draftId,
      filename,
      title: displayTitle,
      tournament: draftData.tournament || '',
      stage: draftData.stage || '',
      date: draftData.date || draftData.createdAt,
      updatedAt: draftData.updatedAt,
      createdAt: draftData.createdAt,
      scenariosFirstPick: draftData.scenariosFirstPick ? true : false,
      scenariosSecondPick: draftData.scenariosSecondPick ? true : false
    };
    
    // Save updated tracking info
    localStorage.setItem(DRAFT_TRACKING_KEY, JSON.stringify(tracking));
    console.log('Draft tracking updated:', draftId);
  } catch (error) {
    console.error('Error tracking draft in localStorage:', error);
  }
};

/**
 * Get draft tracking information
 */
const getDraftTracking = () => {
  try {
    const trackingJson = localStorage.getItem(DRAFT_TRACKING_KEY);
    return trackingJson ? JSON.parse(trackingJson) : {};
  } catch (error) {
    console.error('Error getting draft tracking from localStorage:', error);
    return {};
  }
};

/**
 * Get all saved drafts (returns tracking info only)
 * 
 * @returns {Array} - Array of draft tracking objects
 */
const getDrafts = () => {
  const tracking = getDraftTracking();
  return Object.values(tracking).sort((a, b) => {
    // Sort by updatedAt in descending order (newest first)
    return new Date(b.updatedAt) - new Date(a.updatedAt);
  });
};

/**
 * Save the selected folder path for future use
 * 
 * @param {string} folderPath - The path of the selected folder
 */
const saveSelectedFolder = (folderPath) => {
  try {
    localStorage.setItem(DRAFT_FOLDER_KEY, folderPath);
  } catch (error) {
    console.error('Error saving folder path:', error);
  }
};

/**
 * Get the saved folder path
 * 
 * @returns {string|null} - The saved folder path or null if not set
 */
const getSavedFolderPath = () => {
  try {
    return localStorage.getItem(DRAFT_FOLDER_KEY);
  } catch (error) {
    console.error('Error getting saved folder path:', error);
    return null;
  }
};

/**
 * Process draft files from a directory handle
 * 
 * @param {DirectoryHandle} directoryHandle - The directory handle to scan
 * @returns {Promise<Array>} - Array of draft files found
 */
const processDraftFiles = async (directoryHandle) => {
  // Array to store found draft files
  const draftFiles = [];
  
  // Try to save the folder path if possible
  try {
    if (directoryHandle.name) {
      saveSelectedFolder(directoryHandle.name);
    }
    
    // Try to store the directory handle for future use
    try {
      // Request permission to remember the handle
      const permission = await directoryHandle.requestPermission({ mode: 'read' });
      if (permission === 'granted') {
        // Store the handle in localStorage (will be serialized)
        localStorage.setItem(DRAFT_FOLDER_HANDLE_KEY, JSON.stringify(directoryHandle));
      }
    } catch (permError) {
      console.warn('Could not save directory handle:', permError);
    }
  } catch (error) {
    console.warn('Could not save folder path:', error);
  }
  
  // Iterate through files in the directory
  try {
    for await (const [name, fileHandle] of directoryHandle.entries()) {
      // Check if it's a file and has .json extension
      if (fileHandle.kind === 'file' && name.toLowerCase().endsWith('.json')) {
        try {
          // Get the file
          const file = await fileHandle.getFile();
          
          // Read the file content
          const text = await file.text();
          let draftData;
          
          try {
            draftData = JSON.parse(text);
          } catch (parseError) {
            console.warn(`File ${name} is not valid JSON:`, parseError);
            continue; // Skip this file
          }
          
          // If it has any of the expected draft structure, add it to our list
          if (draftData && (draftData.id || draftData.scenariosFirstPick || draftData.scenariosSecondPick)) {
            // Generate an ID if none exists
            if (!draftData.id) {
              draftData.id = `draft_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            }
            
            // Add missing fields if necessary
            draftData.createdAt = draftData.createdAt || draftData.date || new Date().toISOString();
            draftData.updatedAt = draftData.updatedAt || draftData.createdAt;
            
            // Generate display title that matches filename format
            const cleanString = (str) => {
              return str ? str.replace(/[^a-z0-9]/gi, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') : '';
            };
            
            const teamName = cleanString(draftData.teamName) || 'Team';
            const tournamentName = cleanString(draftData.tournament) || 'Tournament';
            const stageName = cleanString(draftData.stage) || 'Stage';
            const dateString = draftData.date || new Date().toISOString().slice(0, 10);
            
            const displayTitle = `${teamName}_${tournamentName}_${stageName}_${dateString}`;
            draftData.title = displayTitle;
            
            // Update tracking
            trackSavedDraft(draftData.id, name, draftData);
            
            draftFiles.push({
              id: draftData.id,
              filename: name,
              title: displayTitle,
              tournament: draftData.tournament || '',
              date: draftData.date || draftData.createdAt,
              updatedAt: draftData.updatedAt,
              createdAt: draftData.createdAt,
              fileHandle: fileHandle // Store the file handle for direct access later
            });
            
            console.log(`Found draft file: ${name}`);
          } else {
            console.log(`File ${name} is not a draft file (missing required structure)`);
          }
        } catch (error) {
          console.warn(`Error reading file ${name}:`, error);
          // Continue with next file
        }
      }
    }
  } catch (error) {
    console.error('Error iterating through directory:', error);
    throw error;
  }
  
  console.log(`Found ${draftFiles.length} draft files`);
  return draftFiles;
};

/**
 * Scan for draft files in the Downloads directory
 * This uses the File System Access API if available
 * 
 * @returns {Promise<Array>} - Array of draft files found
 */
const scanDownloadsForDrafts = async () => {
  try {
    // Check if File System Access API is available
    if (!window.showDirectoryPicker) {
      throw new Error('File System Access API not supported in this browser');
    }
    
    // Ask user to select their Downloads directory
    const directoryHandle = await window.showDirectoryPicker({
      id: 'downloads',
      startIn: 'downloads',
    });
    
    return await processDraftFiles(directoryHandle);
  } catch (error) {
    console.error('Error scanning for draft files:', error);
    throw error;
  }
};

/**
 * Get a specific draft by ID
 * 
 * @param {string} draftId - The ID of the draft to get
 * @param {string} filename - Optional filename to help identify the draft
 * @returns {Promise<Object>} - The draft data
 */
const getDraft = async (draftId, filename) => {
  try {
    // First check if we have the draft data cached in localStorage
    const cachedDraftJson = localStorage.getItem(`draft_cache_${draftId}`);
    if (cachedDraftJson) {
      try {
        return JSON.parse(cachedDraftJson);
      } catch (error) {
        console.warn('Error parsing cached draft data:', error);
      }
    }
    
    // If we have tracking info, use that to help identify the draft
    const tracking = getDraftTracking();
    const draftInfo = tracking[draftId];
    
    if (!draftInfo) {
      console.warn(`Draft with ID ${draftId} not found in tracking`);
      throw new Error('Draft not found in tracking. Please import it again.');
    }
    
    // If we don't have cached data, we need to prompt the user to select the file
    // This is a fallback only if the cache doesn't exist
    console.log('No cached data found, prompting for file selection');
    const draftData = await loadDraftFile(filename);
    
    if (draftId && draftData.id !== draftId) {
      console.warn('Loaded draft ID does not match requested ID');
    }
    
    return draftData;
  } catch (error) {
    console.error('Error getting draft:', error);
    throw error;
  }
};

/**
 * Load a draft file from user's computer
 * 
 * @param {string} suggestedFilename - Optional filename to suggest to the user
 * @returns {Promise<Object>} - The loaded draft data
 */
const loadDraftFile = (suggestedFilename) => {
  return new Promise((resolve, reject) => {
    // Create a file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.json';
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);
    
    // Handle file selection
    fileInput.onchange = (event) => {
      const file = event.target.files[0];
      if (!file) {
        document.body.removeChild(fileInput);
        reject(new Error('No file selected'));
        return;
      }
      
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const draftData = JSON.parse(e.target.result);
          document.body.removeChild(fileInput);
          
          // Update tracking info
          if (draftData.id) {
            trackSavedDraft(
              draftData.id, 
              file.name, 
              draftData
            );
            
            // Cache the draft data for future quick loading
            try {
              localStorage.setItem(`draft_cache_${draftData.id}`, e.target.result);
            } catch (cacheError) {
              console.warn('Could not cache draft data', cacheError);
            }
          }
          
          resolve(draftData);
        } catch (error) {
          document.body.removeChild(fileInput);
          reject(new Error('Invalid draft file format'));
        }
      };
      
      reader.onerror = () => {
        document.body.removeChild(fileInput);
        reject(new Error('Error reading file'));
      };
      
      reader.readAsText(file);
    };
    
    // Trigger the file selection dialog
    fileInput.click();
  });
};

/**
 * Load a draft file directly using its file handle
 * 
 * @param {FileSystemFileHandle} fileHandle - The file handle to load
 * @returns {Promise<Object>} - The loaded draft data
 */
const loadDraftFromHandle = async (fileHandle) => {
  try {
    // Get the file
    const file = await fileHandle.getFile();
    
    // Read the file content
    const text = await file.text();
    const draftData = JSON.parse(text);
    
    // Update tracking
    if (draftData.id) {
      trackSavedDraft(
        draftData.id, 
        file.name, 
        draftData
      );
    }
    
    return draftData;
  } catch (error) {
    console.error('Error loading draft from file handle:', error);
    throw error;
  }
};

/**
 * Delete a draft from tracking (can't delete from filesystem)
 * 
 * @param {string} draftId - The ID of the draft to remove from tracking
 * @returns {boolean} - Whether the deletion was successful
 */
const deleteDraft = (draftId) => {
  try {
    const tracking = getDraftTracking();
    if (!tracking[draftId]) {
      return false;
    }
    
    // Remove the tracking entry
    delete tracking[draftId];
    
    // Save updated tracking
    localStorage.setItem(DRAFT_TRACKING_KEY, JSON.stringify(tracking));
    
    return true;
  } catch (error) {
    console.error('Error removing draft from tracking:', error);
    return false;
  }
};

/**
 * Try to directly access the Downloads folder without user selection
 * This only works in browsers that support the File System Access API
 * 
 * @returns {Promise<Array>} - Array of draft files found
 */
const scanDefaultDownloadsFolder = async () => {
  try {
    // Check if File System Access API is available
    if (!window.showDirectoryPicker) {
      throw new Error('File System Access API not supported in this browser');
    }
    
    let directoryHandle;
    
    try {
      // Try to access a previously selected directory handle
      const savedHandleJson = localStorage.getItem(DRAFT_FOLDER_HANDLE_KEY);
      if (savedHandleJson) {
        try {
          const savedHandle = JSON.parse(savedHandleJson);
          // Verify we still have permission
          const permission = await savedHandle.requestPermission({ mode: 'read' });
          if (permission === 'granted') {
            directoryHandle = savedHandle;
          }
        } catch (error) {
          console.warn('Could not use saved directory handle:', error);
        }
      }
      
      // If we couldn't get a saved handle, try the downloads directory
      if (!directoryHandle) {
        directoryHandle = await navigator.storage.getDirectory();
      }
    } catch (error) {
      console.warn('Could not access default downloads folder:', error);
      // Fall back to manual selection
      directoryHandle = await window.showDirectoryPicker({
        id: 'downloads',
        startIn: 'downloads',
      });
    }
    
    return await processDraftFiles(directoryHandle);
  } catch (error) {
    console.error('Error scanning default downloads folder:', error);
    throw error;
  }
};

export default {
  saveDraft,
  getDrafts,
  getDraft,
  loadDraftFile,
  loadDraftFromHandle,
  scanDownloadsForDrafts,
  scanDefaultDownloadsFolder,
  deleteDraft,
  saveSelectedFolder,
  getSavedFolderPath,
  getDraftTracking
}; 